import React from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Landmark,
  ShieldCheck,
  Headphones,
  GraduationCap,
  Globe,
  Play,
  Plane,
  Users
} from "lucide-react";

const industryCards = [
  {
    id: "health-care",
    icon: Heart,
    title: "Health Care & Public Health",
    description: "Improve patient understanding with multilingual intake, discharge instructions, and audio guidance—so care plans are clearer and follow-ups are easier."
  },
  {
    id: "government",
    icon: Landmark,
    title: "Government & Public Services",
    description: "Make services accessible with translated forms, citizen support content, and multilingual announcements—reducing confusion and increasing completion rates."
  },
  {
    id: "fintech",
    icon: ShieldCheck,
    title: "Financial Services & Fintech",
    description: "Support onboarding, KYC, and customer help in local languages—so customers understand terms, verify identity smoothly, and trust the process."
  },
  {
    id: "telecom",
    icon: Headphones,
    title: "Telecom & Customer Support",
    description: "Reduce language barriers in support centers with speech transcription, multilingual knowledge bases, and localized self-service flows."
  },
  {
    id: "education",
    icon: GraduationCap,
    title: "Education & EdTech",
    description: "Deliver inclusive learning with translated lessons, pronunciation support, and multilingual parent communication—so learners and families stay engaged."
  },
  {
    id: "ngo",
    icon: Globe,
    title: "NGOs & Humanitarian Services",
    description: "Share critical information clearly in communities’ preferred languages—across outreach, training, and crisis communications."
  },
  {
    id: "media",
    icon: Play,
    title: "Media, Content & Entertainment",
    description: "Create subtitles, captions, transcripts, and localized content faster—while keeping meaning and cultural context intact."
  },
  {
    id: "travel",
    icon: Plane,
    title: "Travel, Hospitality & Transportation",
    description: "Support guests and passengers with multilingual instructions, signage, and service interactions—improving safety and experience end-to-end."
  },
  {
    id: "hr",
    icon: Users,
    title: "HR, Workforce & Training",
    description: "Localize onboarding, safety training, and workplace communication for multilingual teams—so instructions are understood and retention improves."
  }
];

const WhereLanguageMeets = () => {
  return (
    <section className="w-full bg-[#EAEFEA] py-16 sm:py-24">
      <div className="max-w-9xl mx-auto px-6 sm:px-10 lg:px-14">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14" data-aos="fade-up">
          <span className="text-[#E89218] text-xs sm:text-sm font-bold tracking-widest uppercase block mb-3 font-['Axiforma',sans-serif]">
            INDUSTRIES WE SERVE
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-[46px] font-extrabold text-[#0D3429] tracking-tight leading-tight font-['Axiforma',sans-serif] mb-4">
            Where Language Meets Every Industry
          </h2>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto font-normal">
            No matter your sector, clear communication drives results. NKENNEAi helps you connect with users, customers, and communities in the languages they understand best.
          </p>
        </div>

        {/* 3x3 Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-7 lg:gap-8" data-aos="fade-up">
          {industryCards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.id}
                className="bg-white rounded-2xl sm:rounded-[26px] p-6 sm:p-8 flex flex-col justify-between shadow-sm border border-[#E0E7E2] hover:shadow-md hover:border-[#1E6551]/30 transition-all duration-300 group"
              >
                <div>
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-xl bg-[#E8F3EE] text-[#1E6551] flex items-center justify-center mb-5 group-hover:scale-105 transition-transform duration-300">
                    <Icon size={20} />
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-lg sm:text-[20px] text-[#0D3429] mb-3 font-['Axiforma',sans-serif] leading-snug">
                    {card.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 font-normal">
                    {card.description}
                  </p>
                </div>

                {/* Learn More Link */}
                <div className="pt-2">
                  <Link
                    to={`/industries-we-serve#${card.id}`}
                    className="text-[#1E6551] hover:text-[#0D3429] font-bold text-sm inline-flex items-center gap-1 group-hover:underline transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhereLanguageMeets;
